import { useEffect, useId, useRef, useState } from "react";
import ReactQuill from "react-quill";
import DOMPurify from "dompurify";
import PublicationEmojiPicker from "./PublicationEmojiPicker";
import "react-quill/dist/quill.snow.css";

const modules = { toolbar: [["bold", "italic", "underline", "strike"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]] };
function editorValue(value) {
  if (!value) return "";
  if (/<\/?(?:p|br|strong|em|b|i|u|s|ul|ol|li|a|blockquote|h[1-6])\b/i.test(value)) return DOMPurify.sanitize(value);
  return `<p>${value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/\n/g, "<br>")}</p>`;
}
export default function OutreachRichText({ title, value, onChange, readOnly = false }) {
  const id = useId(), editor = useRef(null), selection = useRef(null);
  const lastEmitted = useRef(null);
  // Quill's semantic HTML encodes quotes. Re-sanitizing its live output decodes
  // them and makes the controlled editor reset/re-emit forever on each quote.
  // Recheck the ref on every render, including when Quill emits the same saved
  // value during mount. Memoizing only by value would retain the decoded copy.
  const content = typeof value === "string" && value === lastEmitted.current ? value : editorValue(value);
  const change = html => {
    lastEmitted.current = html;
    onChange(html);
  };
  const [showEmoji, setShowEmoji] = useState(false);
  useEffect(() => {
    const content = editor.current?.getEditor().root;
    if (content) {
      content.setAttribute("role", "textbox");
      content.setAttribute("aria-multiline", "true");
      content.setAttribute("aria-labelledby", id);
    }
  }, [id]);
  const insertEmoji = emoji => {
    const quill = editor.current?.getEditor();
    if (!quill || readOnly) return;
    const range = selection.current || { index: Math.max(0, quill.getLength() - 1), length: 0 };
    if (range.length) quill.deleteText(range.index, range.length, "user");
    quill.insertText(range.index, emoji, "user");
    quill.setSelection(range.index + emoji.length, 0, "user");
    quill.focus();
    setShowEmoji(false);
  };
  return <div className="outreach-rich-field" role="group" aria-labelledby={id}>
    <div className="outreach-editor-heading"><span id={id}>{title}</span><button type="button" aria-label={`Emoji for ${title}`} aria-expanded={showEmoji} disabled={readOnly} onClick={() => setShowEmoji(v => !v)}>☺ Emoji</button></div>
    {showEmoji && <PublicationEmojiPicker onInsert={insertEmoji} />}
    <ReactQuill ref={editor} theme="snow" value={content} onChange={change} onChangeSelection={range => { if (range) selection.current = range; }} modules={modules} readOnly={readOnly} />
  </div>;
}
